import { type CategoryResponse, formatEUR, type TransactionResponse } from '@budget/shared';
import { useState } from 'react';
import { ApiError } from '../api/client';
import { useDeleteTransaction, useUpdateTransaction } from '../api/hooks';
import { AmountField } from './AmountField';
import { BottomSheet } from './BottomSheet';

interface Props {
  entry: TransactionResponse;
  categories: CategoryResponse[];
  onClose: () => void;
}

/**
 * Correction d'une écriture : montant, catégorie et date.
 *
 * La date est celle de la DÉPENSE, pas celle de la saisie. Un oubli rattrapé
 * trois jours plus tard doit pouvoir être reporté au bon jour, sans quoi la
 * courbe raconterait une histoire décalée.
 */
export function TransactionEditModal({ entry, categories, onClose }: Props) {
  const update = useUpdateTransaction();
  const remove = useDeleteTransaction();

  const [amountCents, setAmountCents] = useState(Math.abs(entry.amountCents));
  const [occurredOn, setOccurredOn] = useState(entry.occurredOn);
  const [categoryId, setCategoryId] = useState(entry.categoryId ?? '');
  const [error, setError] = useState<string | null>(null);

  // Le montant d'une dépense financée par un pôle est verrouillé : il doit
  // rester égal à celui du virement jumeau.
  const amountLocked = entry.fundedByGoalId !== null;
  const categorised = entry.type === 'expense' || entry.type === 'income';
  const choices = categories.filter((category) => category.kind === entry.type);

  async function save() {
    setError(null);
    try {
      await update.mutateAsync({
        id: entry.id,
        ...(amountLocked ? {} : { amountCents }),
        occurredOn,
        ...(categorised ? { categoryId: categoryId === '' ? null : categoryId } : {}),
      });
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Modification impossible.');
    }
  }

  async function destroy() {
    setError(null);
    try {
      await remove.mutateAsync(entry.id);
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Suppression impossible.');
    }
  }

  const pending = update.isPending || remove.isPending;

  return (
    <BottomSheet label="Modifier l’écriture" onClose={onClose}>
      <div className="mt-4 flex flex-col gap-4">
        {amountLocked ? (
          <div className="rounded-xl border border-line bg-plane p-3">
            <p className="text-sm">{formatEUR(entry.amountCents)}</p>
            <p className="mt-1 text-xs text-ink-muted">
              Dépense financée par un pôle : son montant est lié à celui du virement qui la
              couvre. Pour le changer, supprimez-la et ressaisissez-la.
            </p>
          </div>
        ) : (
          <AmountField label="Montant" valueCents={amountCents} onChange={setAmountCents} />
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-sm text-ink-secondary">Date de la dépense</span>
          <input
            type="date"
            value={occurredOn}
            onChange={(event) => setOccurredOn(event.target.value)}
            className="rounded-xl border border-line bg-plane px-4 py-3 text-base outline-none focus:border-series"
          />
          <span className="text-xs text-ink-muted">
            Par défaut le jour de la saisie. À corriger si vous rattrapez un oubli.
          </span>
        </label>

        {categorised && (
          <label className="flex flex-col gap-1.5">
            <span className="text-sm text-ink-secondary">Catégorie</span>
            <select
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
              className="rounded-xl border border-line bg-plane px-4 py-3 text-base"
            >
              <option value="">Sans catégorie</option>
              {choices.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.icon} {category.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {error !== null && (
          <p role="alert" className="text-sm text-critical">
            {error}
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-line py-3 text-sm"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={pending || (!amountLocked && amountCents <= 0)}
            onClick={() => void save()}
            className="flex-1 rounded-xl bg-series py-3 text-sm font-medium text-white disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>

        <button
          type="button"
          disabled={pending}
          onClick={() => void destroy()}
          className="rounded-xl border border-line py-2.5 text-sm text-critical disabled:opacity-50"
        >
          Supprimer cette écriture
        </button>
      </div>
    </BottomSheet>
  );
}
